import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Photo from '@mui/icons-material/Photo'
import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import Dialog from '@mui/material/Dialog'
import Grid from '@mui/material/Grid'
import MuiIconButton from '@mui/material/IconButton'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import HHDialogTitle from 'components/common/HHDialogTitle'
import { getExceptionDate, getExceptionDelayTimeFormatted } from 'components/customer-details/content/work-orders/actions/exception/utils'
import ExceptionChip from 'components/exceptions/ExceptionChip'
import ExceptionImageFromPath from 'components/work-order/details/exceptions/ExceptionImageFromPath'
import SwiperContainer from 'components/work-order/details/exceptions/SwiperContainer'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import T from 'T'

import type { SwiperElement } from 'components/work-order/details/exceptions/SwiperContainer'
import type { Swiper } from 'swiper'

type PhotoException = {
  exceptionType?: string | null
  fileLocation?: string | null
  noteText?: string | null
}

type Props = {
  isOpenDrawer?: boolean
  exceptions?: PhotoException[]
  onClose: () => void
}

const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    overflow: 'hidden',
    margin: 0,
    minHeight: '100%',
  },
  '& .MuiDialog-container': {
    justifyContent: 'flex-end',
  },
})

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  opacity: '0.7',
  background: theme.palette.background.paper,
  '&.Mui-disabled': {
    background: theme.palette.background.paper,
  },
  '&:hover': {
    background: theme.palette.background.paper,
  },
  borderWidth: 1,
  borderColor: theme.palette.border.lightGrey,
  borderStyle: 'solid',
  aspectRatio: 1,
  width: 20,
  height: 20,
}))

const WorkOrdersPhotos: React.FC<Props> = ({ isOpenDrawer = false, exceptions = [], onClose }) => {
  const swiperRef = useRef<SwiperElement | null>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'))
  const exception = exceptions[currentIndex]
  const exceptionDate = getExceptionDate(exception)
  const { fullDateTime } = convertDateTimeToPreferedTimezone({ dateTime: exceptionDate })
  const exceptionType = exception?.exceptionType
  const noteText = exception?.noteText === undefined ? '-' : exception.noteText
  const delayTimeFormatted = getExceptionDelayTimeFormatted(exception)

  const handleSwiperPrev = useCallback(() => {
    const swiper = swiperRef?.current?.swiper
    swiper?.slidePrev(300)
    swiper?.update()
  }, [])

  const handleSwiperNext = useCallback(
    (e: React.MouseEvent) => {
      const swiper = swiperRef?.current?.swiper
      e.stopPropagation()
      swiper?.slideNext(300)
      swiper?.update()
    },
    [isOpenDrawer]
  )

  const updateSliceState = (swiper: Swiper) => {
    const { isEnd, isBeginning, realIndex } = swiper
    setIsEnd(isEnd)
    setIsBeginning(isBeginning)
    setCurrentIndex(realIndex)
  }

  useEffect(() => {
    return () => {
      setIsEnd(false)
      setIsBeginning(true)
      setCurrentIndex(0)
    }
  }, [isOpenDrawer, swiperRef])

  return (
    <StyledDialog
      sx={{
        zIndex: theme.zIndex.snackbar,
      }}
      fullScreen={isTabletOrMobile}
      fullWidth={!isTabletOrMobile}
      maxWidth="md"
      open={isOpenDrawer}
      onClose={onClose}
    >
      <HHDialogTitle title={T.WO_PHOTOS} showCloseIcon onClose={onClose} />
      <Box height={680} overflow="hidden" position="relative">
        {isOpenDrawer && (
          <SwiperContainer
            swiperRef={swiperRef}
            sx={{
              height: '100%',
              'swiper-container': { height: '100%' },
              'swiper-slide': { height: '100%' },
              '.swiper-pagination-bullet': {
                opacity: 0.5,
                background: theme.palette.common.white,
              },
              '.swiper-pagination-bullet-active': {
                opacity: 1,
                background: theme.palette.common.white,
              },
            }}
            onReachEnd={updateSliceState}
            onSlideChange={updateSliceState}
          >
            {Array.isArray(exceptions) && exceptions.length === 0 && (
              <Box bgcolor={grey[200]} height="100%" width="100%" display="flex" alignItems="center" justifyContent="center">
                <Photo sx={{ fontSize: 40, color: grey[600] }} />
              </Box>
            )}
            {Array.isArray(exceptions) &&
              exceptions.length > 0 &&
              exceptions.map(slideException => (
                <ExceptionImageFromPath
                  key={slideException.fileLocation}
                  withSwiperSlide
                  exceptionType={exceptionType}
                  height="100%"
                  minHeight="675px"
                  path={slideException.fileLocation}
                  size="medium"
                />
              ))}
          </SwiperContainer>
        )}

        {Array.isArray(exceptions) && exceptions.length > 1 && (
          <>
            <Box bottom="calc(50% - 12px)" left="4px" margin="0 auto" marginRight="-28px" position="absolute" zIndex={1}>
              <IconButton disabled={isBeginning} onClick={handleSwiperPrev}>
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Box bottom="calc(50% - 12px)" margin="0 auto" marginLeft="-28px" position="absolute" right="4px" zIndex={1}>
              <IconButton disabled={isEnd} sx={{ ml: 1 }} onClick={handleSwiperNext}>
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </>
        )}
      </Box>
      <Box mt={2} mx={2}>
        <Grid container rowGap={1}>
          <Grid container columnGap={2} size={12}>
            <Grid size={12}>
              <Typography color="textSecondary" fontWeight={500}>
                {T.DATE_AND_TIME}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography>{`${fullDateTime || '-'}`}</Typography>
            </Grid>
          </Grid>
          <Grid container columnGap={2} size={12}>
            <Grid size={12}>
              <Typography color="textSecondary" fontWeight={500}>
                {T.EXCEPTION}
              </Typography>
            </Grid>
            <Grid display="flex" alignItems="center" size={12}>
              <ExceptionChip exception={exceptionType} />
              {delayTimeFormatted && (
                <Typography ml={2} display="inline" variant="h6" fontWeight={400} bgcolor={grey[100]} p={0.75} borderRadius={1}>
                  {delayTimeFormatted}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid container columnGap={2} size={12}>
            <Grid size={12}>
              <Typography color="textSecondary" fontWeight={500}>
                {T.NOTE}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography>{`${noteText || '-'}`}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </StyledDialog>
  )
}

export default WorkOrdersPhotos
